import { ReduxState } from '@/redux/modules/types';
import GenericContact from '@/types/generic-contact';
import { createSelector } from '@reduxjs/toolkit';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

// Base slices
const selectTaxes = (state: ReduxState) => state.taxes;
const selectAuth = (state: ReduxState) => state.auth;

// Memoized selectors
const selectTaxRates = createSelector([selectTaxes], taxes => {
  if (taxes == null) {
    return undefined;
  }
  return taxes.taxRates;
});

const selectCurrentUser = createSelector([selectAuth], auth => auth.user);

/**
 * Selects the current tax rate based on the provided state and optional contact.
 *
 * Note that this function will return undefined if the contact has a province
 * that is not in the tax rates object.
 *
 * @param {ReduxState} state - The current state of the Redux store.
 * @param {GenericContact} [contact] - An optional contact object that may contain a province.
 * @returns {number} - The tax rate for the specified province if the user has permission and tax rates are available, otherwise returns 0.
 */
export const selectCurrentTaxRate = (state: ReduxState, contact?: GenericContact) => {
  const taxRates = selectTaxRates(state);
  const user = selectCurrentUser(state);

  const hasTaxesPermission = userHasPermission(user, PERMISSIONS.TAXES);
  const hasTaxRates = taxRates != null;
  if (contact && contact.province != null && hasTaxesPermission && hasTaxRates) {
    return taxRates.CA[contact.province];
  }
  return 0;
};
